import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FiX } from 'react-icons/fi';
import { Sheet } from '../../../../../components/Sheet/Sheet';
import DatePickerWrapper from '../../../../../components/DatePickerWrapper/DatePickerWrapper';
import * as translator from '../../../../../utils/translator';
import { createLogWeightSchema, LogWeightFormValues } from './logWeightSchema';
import WeightInput from './WeightInput';
import { WeightUnit } from '../../../../../utils/weightUnits';

interface LogWeightSheetProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: LogWeightFormValues) => void;
	defaultWeight: number | null;
	language: string;
	loading: boolean;
	submitError: string | null;
	planStartDate?: string | null;
	weightUnit: WeightUnit;
	allowUnitChange: boolean;
	onWeightUnitChange: (unit: WeightUnit) => void;
}

// planStartDate is the subscription's created_at — a full timestamp (e.g. "2026-08-11 13:39:27"),
// i.e. the moment the customer bought the plan. The date picker hands us local midnight for each
// calendar day, so comparing against the raw timestamp would also reject the start day itself
// (midnight 11 Aug < 13:39 11 Aug). Normalise to the start of that day so entries are allowed from
// the plan start date onward, inclusive.
const startOfPlanDay = (planStartDate: string | null | undefined): Date | null => {
	if (!planStartDate) return null;
	// Use the date part only, so "YYYY-MM-DD HH:mm:ss" isn't parsed as a UTC-shifted instant.
	const [datePart] = String(planStartDate).split(/[ T]/);
	const [year, month, day] = datePart.split('-').map(Number);
	if (!year || !month || !day) return null;
	return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const LogWeightSheet: React.FC<LogWeightSheetProps> = ({ isOpen, onClose, onSubmit, defaultWeight, language, loading, submitError, planStartDate, weightUnit, allowUnitChange, onWeightUnitChange }) => {
	const planStart = startOfPlanDay(planStartDate);
	const schema = createLogWeightSchema(language);

	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
	} = useForm<LogWeightFormValues>({
		resolver: zodResolver(schema),
		mode: 'onSubmit',
		defaultValues: { weightKg: defaultWeight ?? undefined, loggedAt: '' },
	});

	// Bumped after each reset so WeightInput remounts from the reset value (it keeps its own text).
	const [inputKey, setInputKey] = React.useState(0);

	React.useEffect(() => {
		if (isOpen) {
			reset({ weightKg: defaultWeight ?? undefined, loggedAt: '' });
			setInputKey((key) => key + 1);
		}
	}, [isOpen]);

	const submitHandler = (values: LogWeightFormValues) => {
		onSubmit({ weightKg: values.weightKg, loggedAt: values.loggedAt || undefined });
	};

	return (
		<Sheet isOpen={isOpen} onClose={onClose}>
			<Sheet.Header className="px-[21px] pt-4">
				<div className="flex items-center justify-between w-full">
					<p className="text-[16px] font-semibold text-care-plus-heading leading-[1.2]">
						{translator.translate(language, 'my_account', 'CareProfile/LogWeight/Title')}
					</p>
					<button
						id="log-weight-close"
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="flex items-center justify-center w-[31px] h-[31px] rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiX size={19} />
					</button>
				</div>
			</Sheet.Header>

			<Sheet.Body className="px-[21px] py-6">
				<form id="log-weight-form" className="flex flex-col gap-y-8" onSubmit={handleSubmit(submitHandler)}>
					<div className="flex flex-col gap-y-4">
						<div id="log-weight-date-container">
							<p className="text-xs text-[#9D9D9D] mb-1">{translator.translate(language, 'my_account', 'CareProfile/LogWeight/DateLabel')}</p>
							<Controller
								control={control}
								name="loggedAt"
								render={({ field }) => (
									<DatePickerWrapper
										id="log-weight-date"
										value={field.value}
										errorMode={Boolean(errors.loggedAt)}
										isBirthdayInput={false}
										placeholderText={translator.translate(language, 'my_account', 'CareProfile/LogWeight/Today')}
										// Entries are only valid from the plan start date onward. minDate greys out
										// (and blocks navigating to) anything earlier; filterDate caps the top end at today.
										minDate={planStart}
										filterDate={(date: Date) => {
											if (planStart && date < planStart) return false;
											return date <= new Date();
										}}
										onChange={(value: string) => field.onChange(value)}
									/>
								)}
							/>
							{errors.loggedAt && <p className="text-red-500 text-xs mt-1">{errors.loggedAt.message}</p>}
						</div>

						<div id="log-weight-weight-container">
							<Controller
								control={control}
								name="weightKg"
								render={({ field }) => (
									<WeightInput
										key={inputKey}
										id="log-weight-weight"
										language={language}
										unit={weightUnit}
										allowUnitChange={allowUnitChange}
										onUnitChange={onWeightUnitChange}
										valueKg={field.value ?? undefined}
										onChangeKg={field.onChange}
										onBlur={field.onBlur}
										kgPlaceholder={translator.translate(language, 'my_account', 'CareProfile/LogWeight/WeightLabel')}
										errorMsg={errors.weightKg?.message ?? ''}
									/>
								)}
							/>
						</div>

						{submitError && <p className="text-red-500 text-xs">{submitError}</p>}
					</div>

					<button
						id="log-weight-submit"
						type="submit"
						disabled={loading}
						className="w-full bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white text-[16px] font-medium py-3.5 rounded-[16px] border-none transition-all duration-200 disabled:opacity-70 cursor-pointer"
					>
						{loading
							? translator.translate(language, 'default', 'SubmitButton/loading_text')
							: translator.translate(language, 'my_account', 'CareProfile/LogWeight/Submit')}
					</button>
				</form>
			</Sheet.Body>
		</Sheet>
	);
};

export default LogWeightSheet;